import { SITE, telLink, mailLink } from "../data/siteConfig";
import { useSEO } from "../components/SEO";
import FadeIn from "../components/FadeIn";

const LegalLayout = ({ title, children }) => (
  <div>
    <section className="bg-[#0B3056] py-14">
      <div className="mx-auto max-w-4xl px-4 sm:px-6">
        <FadeIn>
          <h1 className="font-heading text-3xl font-extrabold tracking-tight text-white sm:text-4xl">{title}</h1>
        </FadeIn>
      </div>
    </section>
    <section className="mx-auto max-w-4xl px-4 py-14 sm:px-6">
      <FadeIn>
        <div className="space-y-8 text-base leading-relaxed text-slate-600">{children}</div>
      </FadeIn>
    </section>
  </div>
);

const H2 = ({ children }) => (
  <h2 className="font-heading text-xl font-bold text-[#0B3056]">{children}</h2>
);

export const PrivacyPolicyPage = () => {
  useSEO("Privacy Policy | Dr Lal PathLabs Collection Center Bhopal", "Privacy policy of Dr Lal PathLabs Collection Center Bhopal.");
  return (
    <LegalLayout title="Privacy Policy">
      <p>
        This Privacy Policy explains how {SITE.fullName}, Bhopal (&quot;we&quot;, &quot;our&quot;) handles information
        submitted through this website.
      </p>
      <div>
        <H2>Information We Collect</H2>
        <p className="mt-2">
          When you submit an enquiry or booking form, we collect the details you provide — such as your name, mobile
          number, email address, the test or service you are enquiring about, your preferred date and time, and your
          message. We collect only what is needed to respond to your enquiry.
        </p>
      </div>
      <div>
        <H2>How We Use Your Information</H2>
        <p className="mt-2">
          Your information is used solely to contact you regarding your enquiry or appointment, confirm availability,
          and coordinate sample collection services. We do not sell, rent, or publicly share your personal information.
        </p>
      </div>
      <div>
        <H2>Sensitive Health Information</H2>
        <p className="mt-2">
          Please do not submit medical reports, prescriptions, or other sensitive health information through the
          general contact or booking forms unless specifically requested through a secure channel.
        </p>
      </div>
      <div>
        <H2>Data Security</H2>
        <p className="mt-2">
          Enquiry details are stored securely and are accessible only to authorized center staff. We apply reasonable
          technical and organizational measures to protect your information.
        </p>
      </div>
      <div>
        <H2>Third-Party Services</H2>
        <p className="mt-2">
          This website may use third-party services such as website analytics and WhatsApp messaging links. These
          services are governed by their own privacy policies.
        </p>
      </div>
      <div>
        <H2>Your Rights</H2>
        <p className="mt-2">
          You may request access to, correction of, or deletion of your enquiry information at any time by contacting
          us at <a href={mailLink} className="font-semibold text-[#1E88E5]">{SITE.email}</a> or{" "}
          <a href={telLink} className="font-semibold text-[#1E88E5]">{SITE.phone}</a>.
        </p>
      </div>
      <p className="text-sm text-slate-500">Last updated: July 2026.</p>
    </LegalLayout>
  );
};

export const TermsPage = () => {
  useSEO("Terms & Conditions | Dr Lal PathLabs Collection Center Bhopal", "Terms and conditions of using the Dr Lal PathLabs Collection Center Bhopal website.");
  return (
    <LegalLayout title="Terms &amp; Conditions">
      <p>
        By using this website, you agree to the following terms. If you do not agree, please do not use this website.
      </p>
      <div>
        <H2>Purpose of This Website</H2>
        <p className="mt-2">
          This website provides information about the diagnostic sample collection services of {SITE.fullName},
          Bhopal, and allows visitors to submit test and appointment enquiries. It is an independent collection center
          website and not the corporate website of Dr Lal PathLabs Ltd.
        </p>
      </div>
      <div>
        <H2>Enquiries and Appointments</H2>
        <p className="mt-2">
          Submitting a form on this website sends an enquiry — it does not automatically confirm an appointment. Our
          team will contact you to confirm availability, prices and appointment details. Test availability, packages
          and prices are subject to change and are confirmed at the time of enquiry.
        </p>
      </div>
      <div>
        <H2>No Medical Advice</H2>
        <p className="mt-2">
          Content on this website is for general information only. It does not provide medical diagnosis, treatment, or
          interpretation of test results. Please consult a qualified healthcare professional for medical advice.
        </p>
      </div>
      <div>
        <H2>Acceptable Use</H2>
        <p className="mt-2">
          You agree not to misuse this website, submit false information, or attempt to disrupt its operation. Form
          submissions are protected by anti-spam measures.
        </p>
      </div>
      <div>
        <H2>Limitation of Liability</H2>
        <p className="mt-2">
          While we strive to keep information accurate and up to date, we make no warranties about completeness or
          accuracy. To the fullest extent permitted by law, we are not liable for any loss arising from use of this
          website.
        </p>
      </div>
      <div>
        <H2>Contact</H2>
        <p className="mt-2">
          Questions about these terms? Contact us at <a href={mailLink} className="font-semibold text-[#1E88E5]">{SITE.email}</a> or{" "}
          <a href={telLink} className="font-semibold text-[#1E88E5]">{SITE.phone}</a>.
        </p>
      </div>
      <p className="text-sm text-slate-500">Last updated: July 2026.</p>
    </LegalLayout>
  );
};

export const DisclaimerPage = () => {
  useSEO("Medical Disclaimer | Dr Lal PathLabs Collection Center Bhopal", "Medical disclaimer of Dr Lal PathLabs Collection Center Bhopal.");
  return (
    <LegalLayout title="Medical Disclaimer">
      <p>
        This website is intended for general information about diagnostic collection services. It does not provide
        medical diagnosis or treatment. Please consult a qualified healthcare professional for medical advice.
      </p>
      <div>
        <H2>No Doctor-Patient Relationship</H2>
        <p className="mt-2">
          Using this website or submitting an enquiry does not create a doctor-patient relationship. Test descriptions
          on this website are general in nature and are not a substitute for professional medical guidance.
        </p>
      </div>
      <div>
        <H2>Test Results</H2>
        <p className="mt-2">
          Laboratory reports should always be interpreted by a qualified healthcare professional who knows your medical
          history. Never start, stop, or change any medication or treatment based solely on a laboratory report.
        </p>
      </div>
      <div>
        <H2>Emergencies</H2>
        <p className="mt-2">
          This website is not for medical emergencies. If you are experiencing a medical emergency, please call
          emergency services or visit the nearest hospital immediately.
        </p>
      </div>
      <p className="text-sm text-slate-500">Last updated: July 2026.</p>
    </LegalLayout>
  );
};
